// ── Design decisions ──
// Tarjeta de caja. Vive en la sección colapsable de cajas de AgencyStep2 (una
// grilla a ancho completo, repeat(auto-fill, minmax(18rem, 1fr))), ya no en
// un riel lateral. Acá vive el ÚNICO peso del flujo: la operadora pesa y mide
// la caja física; el peso nunca toca el Anexo.
//
// · Campos: peso (lbs), dimensiones y una casilla por paquete. Un paquete
//   pertenece a lo sumo a una caja: si ya está en otra, sigue visible pero
//   deshabilitado y rotulado "en Caja N".
// · La tarjeta no guarda nada: cada cambio sube por onUpdateCaja y el padre
//   decide cuándo autoguardar (una vez válida: peso > 0, dimensiones y ≥1
//   paquete). Mientras falte algo se muestra una nota ámbar que nombra qué
//   falta; nunca un alert.
// · Cuando los paquetes de la caja son de destinatarios distintos se avisa en
//   ámbar que el despacho usa el destinatario principal (el del primer
//   paquete). Informa, no bloquea.
// · Estados visibles: Guardada / Guardando… / error del guardado.

import { X } from 'lucide-react';

const CajaCard = ({ caja, paquetes = [], cajas = [], onUpdateCaja, onDeleteCaja, canDelete = true }) => {
  const peso = parseFloat(caja.weight_lb);
  const faltantes = [];
  if (!(peso > 0)) faltantes.push('peso');
  if (!String(caja.dimensions || '').trim()) faltantes.push('dimensiones');
  if (!caja.packageIds || caja.packageIds.length === 0) faltantes.push('al menos un paquete');

  const cajaDe = (pkgId) => cajas.find((c) => c.localId !== caja.localId && (c.packageIds || []).includes(pkgId)) || null;

  const enEsta = paquetes.filter((p) => (caja.packageIds || []).includes(p.id));
  const destinatarios = new Set(enEsta.map((p) => String(p.recipient?.name || '').trim()).filter(Boolean));
  const mixta = destinatarios.size > 1;

  const patch = (p) => onUpdateCaja && onUpdateCaja(caja.localId, p);

  const toggle = (pkgId, checked) => {
    const actual = caja.packageIds || [];
    patch({ packageIds: checked ? [...actual.filter((id) => id !== pkgId), pkgId] : actual.filter((id) => id !== pkgId) });
  };

  return (
    <div className="tile agency-caja" style={{ background: 'var(--bg)', padding: '12px' }} aria-label={`Caja ${caja.caja_number}`} role="group">
      <div className="flex items-center justify-between mb-2 gap-2">
        <span className="row-id">CAJA {caja.caja_number}</span>
        <div className="flex items-center gap-2">
          {caja.saving ? (
            <span className="badge badge-pending">Guardando…</span>
          ) : caja.error ? (
            <span className="badge badge-error" role="alert">
              {caja.error}
            </span>
          ) : caja.saved ? (
            <span className="badge badge-done">Guardada</span>
          ) : null}
          <button
            type="button"
            onClick={() => onDeleteCaja && onDeleteCaja(caja.localId)}
            disabled={!canDelete}
            aria-label={`Eliminar caja ${caja.caja_number}`}
            title="Eliminar caja"
            className="btn-ghost focus-visible:ring-2 focus-visible:ring-[var(--avianca-red)]"
            style={{ color: 'var(--avianca-red)', padding: '4px' }}
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-2">
        <input
          type="number"
          min={0}
          step={0.1}
          inputMode="decimal"
          className="input focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
          placeholder="Peso (lbs)"
          aria-label="Peso de la caja (lbs)"
          value={caja.weight_lb ?? ''}
          onChange={(e) => patch({ weight_lb: e.target.value })}
        />
        <input
          type="text"
          className="input focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
          placeholder="Dimensiones (ej: 12x5x17)"
          aria-label="Dimensiones de la caja"
          value={caja.dimensions ?? ''}
          onChange={(e) => patch({ dimensions: e.target.value })}
        />
      </div>

      <div className="mb-2">
        <p className="metric-label mb-1">Paquetes en esta caja</p>
        {paquetes.length === 0 ? (
          <p className="metric-sub" style={{ color: 'var(--text-tertiary)' }}>
            Todavía no hay paquetes en esta encomienda.
          </p>
        ) : (
          <div style={{ border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--tile)' }}>
            {paquetes.map((p) => {
              const otra = cajaDe(p.id);
              const marcado = (caja.packageIds || []).includes(p.id);
              const nombre = p.recipient?.name || 'Destinatario';
              return (
                <label
                  key={p.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 10px',
                    fontSize: '11px',
                    borderBottom: '1px solid var(--border)',
                    cursor: otra ? 'not-allowed' : 'pointer',
                    opacity: otra ? 0.55 : 1,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={marcado}
                    disabled={Boolean(otra)}
                    aria-label={`Paquete ${p.package_number} — ${nombre}`}
                    className="focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
                    onChange={(e) => toggle(p.id, e.target.checked)}
                  />
                  <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>Paquete {p.package_number}</span>
                  <span className="truncate" style={{ color: 'var(--text-secondary)', minWidth: 0 }}>
                    — {nombre}
                  </span>
                  {otra && (
                    <span className="badge badge-cas" style={{ marginLeft: 'auto' }}>
                      en Caja {otra.caja_number}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        )}
      </div>

      {faltantes.length > 0 && (
        <p className="badge badge-hold w-full" style={{ whiteSpace: 'normal', padding: '6px 10px' }} role="status">
          Falta: {faltantes.join(', ')}. La caja se guarda sola cuando esté completa.
        </p>
      )}
      {mixta && (
        <p className="badge badge-hold w-full mt-1" style={{ whiteSpace: 'normal', padding: '6px 10px' }} role="status">
          Esta caja tiene paquetes para {destinatarios.size} destinatarios — el despacho usa el destinatario principal.
        </p>
      )}
    </div>
  );
};

export default CajaCard;
